/**
 * MachineCapturePanel — an operation's machine values and in-process check rows, as the runner recorded them.
 * ==============================================================================================================
 * Fed by GET /bp/run/batches/<bn>/operations/<batch_config_id>/capture/ (services/run/capture.py). Each value shows
 * its source (Captured from a machine tag, or Entered by hand) and the time it occurred on the source; the tag,
 * quality, age and read point sit behind an (i). A check row lists each check with its head values, whether they are
 * within limits, who did it and its status.
 *
 * The client decides nothing: the server reads the tag, judges the value, says when a hand entry is allowed and when
 * a check is due. This panel sends only the person's value, reason and signature, and renders the server's words.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Popover, Space, Spin, Table, Tag, theme } from 'antd';
import { Info, RefreshCw } from 'lucide-react';
import { formatDateTimeSeconds } from '@/utils/datetime';
import { enterCapturedByHand, getOperationCapture, openDueCheck, readCaptured } from '@/utils/capture_api';

const SOURCE_COLOR = { CAPTURED: 'blue', ENTERED: 'orange' };
const STATUS_COLOR = { Due: 'default', Open: 'processing', Recorded: 'success', 'Recorded late': 'warning', Overdue: 'error' };
const verdictColor = (v) => (!v ? undefined : v === 'IN_RANGE' ? 'success' : 'error');
const t = (v) => (v ? formatDateTimeSeconds(v) : '—');

const InfoTip = ({ rows }) => {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!shown.length) return null;
  return (
    <Popover
      trigger={['hover', 'click']}
      content={(
        <div style={{ fontSize: 12, maxWidth: 320 }}>
          {shown.map(([k, v]) => (
            <div key={k} style={{ overflowWrap: 'anywhere' }}><b>{k}</b>: {String(v)}</div>
          ))}
        </div>
      )}
    >
      <Info size={14} style={{ cursor: 'pointer', verticalAlign: 'middle' }} aria-label="Details" />
    </Popover>
  );
};
InfoTip.propTypes = { rows: PropTypes.array.isRequired };

const ValueCell = ({ f }) => {
  const v = f.value;
  if (!v) {
    const live = f.live;
    return (
      <Space size={6} wrap>
        <span style={{ opacity: 0.7 }}>{f.read_at_label || 'Not read'}</span>
        {f.last_read && !f.last_read.fresh ? <Tag color="error">No fresh value</Tag> : null}
        <InfoTip rows={[
          ['Tag', f.tag.ref], ['Machine', f.tag.machine],
          ['Last read', f.last_read ? `${f.last_read.value || '—'} at ${t(f.last_read.source_at)}` : null],
          ['Why', f.last_read?.reason], ['Now on the tag', live ? `${live.value ?? '—'} at ${t(live.source_at)}` : null],
          ['Now', live ? (live.fresh ? 'fresh' : live.reason) : null],
        ]}
        />
      </Space>
    );
  }
  return (
    <Space size={6} wrap>
      <b>{v.value}{v.unit ? ` ${v.unit}` : ''}</b>
      <Tag color={SOURCE_COLOR[v.source]}>{v.source_label}</Tag>
      {v.verdict ? <Tag color={verdictColor(v.verdict)}>{v.verdict.replace(/_/g, ' ').toLowerCase()}</Tag> : null}
      <span style={{ fontSize: 12, opacity: 0.75 }}>{t(v.source_at || v.recorded_at)}</span>
      <InfoTip rows={[
        ['Tag', f.tag.ref], ['Machine', f.tag.machine], ['Read', f.read_at_label],
        ['Quality', v.quality], ['Age at read', v.age_seconds != null ? `${v.age_seconds} s` : null],
        ['Limit', f.limit], ['Recorded by', v.recorded_by], ['Recorded at', t(v.recorded_at)], ['Reason', v.reason],
      ]}
      />
    </Space>
  );
};
ValueCell.propTypes = { f: PropTypes.object.isRequired };

const HandEntry = ({ field, onDone, onCancel }) => {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    const v = await form.validateFields();
    setBusy(true);
    setError(null);
    try {
      const out = await enterCapturedByHand(field.instance_id, {
        value: v.value, reason: v.reason, esign: { username: v.username, password: v.password },
      });
      onDone(out?.panel);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open title={`Enter by hand · ${field.title}`} okText="Enter & sign" onOk={submit} confirmLoading={busy}
      onCancel={onCancel} destroyOnHidden maskClosable={false}>
      {field.last_read?.reason ? <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={field.last_read.reason} /> : null}
      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} /> : null}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="value" label={`Value${field.unit ? ` (${field.unit})` : ''}`} rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} autoFocus />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="username" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input placeholder="Username" autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input.Password placeholder="Password" autoComplete="current-password" />
          </Form.Item>
        </Space.Compact>
      </Form>
    </Modal>
  );
};
HandEntry.propTypes = { field: PropTypes.object.isRequired, onDone: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

const HeadValues = ({ values, onHand }) => (
  <Space size={[4, 4]} wrap>
    {values.map((h) => {
      if (h.field) {
        const v = h.field.value;
        return (
          <Space key={h.instance_id} size={2}>
            <Tag color={v ? verdictColor(v.verdict) : 'default'} style={{ marginInlineEnd: 0 }}>
              {v ? `${v.value}${v.source === 'ENTERED' ? ' ✎' : ''}` : '—'}
            </Tag>
            <InfoTip rows={[
              ['Step', h.title], ['Tag', h.field.tag.ref], ['Source', v?.source_label],
              ['At source', v ? t(v.source_at) : null], ['Quality', v?.quality], ['Reason', v?.reason],
              ['Last read', !v && h.field.last_read ? h.field.last_read.reason : null],
            ]}
            />
            {h.field.fallback_allowed ? <Button size="small" type="link" onClick={() => onHand(h.field)}>Enter</Button> : null}
          </Space>
        );
      }
      return (h.entered || []).map((e) => (
        <Tag key={`${h.instance_id}-${e.label}`}>{e.label}: {e.value || '—'}</Tag>
      ));
    })}
  </Space>
);
HeadValues.propTypes = { values: PropTypes.array.isRequired, onHand: PropTypes.func.isRequired };

const MachineCapturePanel = ({ batchNumber, batchConfigId, initial = null }) => {
  const { token } = theme.useToken();
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [hand, setHand] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getOperationCapture(batchNumber, batchConfigId, signal));
    } catch (e) {
      if (e?.name !== 'CanceledError') setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [batchNumber, batchConfigId]);

  useEffect(() => {
    if (initial) return undefined;
    const ctl = new AbortController();
    load(ctl.signal);
    return () => ctl.abort();
  }, [load, initial]);

  const act = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      const out = await fn();
      if (out?.panel) setData(out.panel);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) return <Spin />;
  const fields = data?.fields || [];
  const rows = data?.check_rows || [];
  if (!fields.length && !rows.length) {
    return error ? <Alert type="error" showIcon message={error} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No machine values on this operation" />;
  }

  const fieldCols = [
    { title: 'Step', key: 'title', render: (_, f) => <span>{f.title}{f.pass ? ` · ${f.pass}` : ''}</span> },
    { title: 'Value', key: 'value', render: (_, f) => <ValueCell f={f} /> },
    {
      title: '', key: 'act', width: 140, render: (_, f) => (
        <Space size={4}>
          {f.fallback_allowed ? <Button size="small" onClick={() => setHand(f)}>Enter by hand</Button> : null}
          {f.read_allowed ? (
            <Button size="small" loading={busy === `r${f.instance_id}`}
              onClick={() => act(`r${f.instance_id}`, () => readCaptured(f.instance_id))}>Read again</Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const checkCols = [
    { title: 'Check', dataIndex: 'label', key: 'label', width: 90 },
    { title: 'Triggered', key: 'trig', width: 150, render: (_, c) => <span style={{ fontSize: 12 }}>{t(c.triggered_at)}</span> },
    { title: 'Values', key: 'values', render: (_, c) => (c.values.length ? <HeadValues values={c.values} onHand={setHand} /> : '—') },
    {
      title: 'Within limits', key: 'ok', width: 105,
      render: (_, c) => (c.within_limits == null ? '—' : <Tag color={c.within_limits ? 'success' : 'error'}>{c.within_limits ? 'Yes' : 'No'}</Tag>),
    },
    { title: 'Done by', key: 'by', width: 140, render: (_, c) => (c.done_by.length ? c.done_by.join(', ') : '—') },
    {
      title: 'Status', key: 'status', width: 150, render: (_, c) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[c.status]}>{c.status}</Tag>
          <InfoTip rows={[['Due by', t(c.due_by)], ['Recorded', t(c.done_at)], ['Review item', c.review_item ? 'raised' : null]]} />
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      {error ? <Alert type="error" showIcon closable message={error} onClose={() => setError(null)} /> : null}
      {fields.length ? (
        <Card size="small" title="Machine values" extra={<Button size="small" type="text" icon={<RefreshCw size={14} />} onClick={() => load()} aria-label="Refresh" />}>
          <Table size="small" rowKey="instance_id" pagination={false} columns={fieldCols} dataSource={fields} scroll={{ x: 'max-content' }} />
        </Card>
      ) : null}
      {rows.map((r) => (
        <Card key={r.instance_id} size="small"
          title={(
            <Space size={6}>
              <span>{r.title}</span>
              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>{r.label}</span>
              <InfoTip rows={[['Counter tag', r.counter_tag], ['Heads', r.heads], ['Due within', r.due_minutes ? `${r.due_minutes} min` : null]]} />
            </Space>
          )}
          extra={r.can_open ? (
            <Button size="small" type="primary" loading={busy === `c${r.instance_id}`}
              onClick={() => act(`c${r.instance_id}`, () => openDueCheck(r.instance_id))}>Open due check</Button>
          ) : null}
        >
          {r.checks.length ? (
            <Table size="small" rowKey="number" pagination={false} columns={checkCols} dataSource={r.checks} scroll={{ x: 'max-content' }} />
          ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No check due yet" />}
        </Card>
      ))}
      {hand ? <HandEntry field={hand} onCancel={() => setHand(null)} onDone={(p) => { setHand(null); if (p) setData(p); else load(); }} /> : null}
    </div>
  );
};

MachineCapturePanel.propTypes = {
  batchNumber: PropTypes.string.isRequired,
  batchConfigId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initial: PropTypes.object,
};

export default MachineCapturePanel;
